/**
 * Renaming and moving files. Project files link to each other by path, so a
 * move rewrites every path that names a moved file, in the same command as
 * the move itself: one undo puts the files and their references back.
 *
 * The links a project has, and how each is written:
 * - project.json `scenes`, and a scene's `preload`, `animations`,
 *   `tilemaps[].file` (a prefab's too), prefab placements and objects' `clips.file`: paths
 *   from the project root. So are project.json's `theme` and the theme's font files.
 * - Asset pack entries (`url`, `atlasURL`, ...): relative to their section's
 *   `path`, as Phaser's loader joins them.
 * - Tiled maps' tileset and image layer images: relative to the map file.
 * - Strings in plugin data (objects' and scenes' `data`, project.json
 *   `settings`) and in script fields that name a scene, prefab, clips,
 *   animations, asset pack or tile map file by its ending: paths from the
 *   root, such as a kit's start room or a close-up's room.
 * Texture and animation keys are names, not paths, so they never change.
 */
import { fileKindFor } from './canonical.js';
import type { Op, PathSegment } from './ops.js';
import { dirname, normalizeProjectPath, resolveFrom } from './paths.js';
import { themePathOf } from './theme.js';
import { soundsPathOf } from './sound.js';

export interface FileMove {
  from: string;
  to: string;
}

export type ReferenceKind = 'scene' | 'pack' | 'animations' | 'tilemap' | 'prefab' | 'clips' | 'theme' | 'sounds' | 'asset' | 'image' | 'data';

/** One place in a JSON file that names another file by path. */
export interface FileReference {
  /** The file holding the link. */
  file: string;
  /** Where in that file. */
  path: PathSegment[];
  /** The path as written. */
  raw: string;
  /** The project path it resolves to. */
  target: string;
  kind: ReferenceKind;
  /** How the link is written: from the project root, a pack section's folder, or the holding file's folder. */
  base: { root: true } | { folder: string } | { relativeToFile: true };
}

export class MoveError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'MoveError';
  }
}

const PACK_URL_FIELDS = ['url', 'textureURL', 'atlasURL', 'fontDataURL', 'normalMap', 'dataURL', 'jsonURL'];

const isObject = (v: unknown): v is Record<string, unknown> => typeof v === 'object' && v !== null && !Array.isArray(v);

function tryNormalize(path: unknown): string | undefined {
  if (typeof path !== 'string') return undefined;
  try {
    return normalizeProjectPath(path);
  } catch {
    return undefined;
  }
}

/** `target` written relative to the folder `folder` ('' for the root), with `..` where needed. */
export function relativePath(folder: string, target: string): string {
  const from = folder.split('/').filter(Boolean);
  const parts = target.split('/');
  let i = 0;
  while (i < from.length && i < parts.length - 1 && from[i] === parts[i]) i++;
  return [...from.slice(i).map(() => '..'), ...parts.slice(i)].join('/');
}

/** Endings of the project files plugin data and script fields can name. */
const DATA_FILE_ENDINGS = ['.scene.json', '.prefab.json', '.clips.json', '.anims.json', '.pack.json', '.tilemap.json'];

/**
 * The strings in a plugin data, plugin settings or script fields value that
 * name a project file, with where each is. Plugins store links there in their
 * own shapes, so they are found by their file ending.
 */
export function dataFileLinks(value: unknown, path: PathSegment[] = []): { path: PathSegment[]; raw: string }[] {
  if (typeof value === 'string') return DATA_FILE_ENDINGS.some((e) => value.endsWith(e)) ? [{ path, raw: value }] : [];
  if (Array.isArray(value)) return value.flatMap((v, i) => dataFileLinks(v, [...path, i]));
  if (isObject(value)) return Object.entries(value).flatMap(([k, v]) => dataFileLinks(v, [...path, k]));
  return [];
}

function isTiledMap(value: unknown): value is { tilesets: unknown[]; layers: unknown[] } {
  return isObject(value) && Array.isArray(value.tilesets) && Array.isArray(value.layers);
}

function isPack(file: string, value: unknown): value is Record<string, unknown> {
  return file.endsWith('.pack.json') && isObject(value);
}

/** Every file reference in the open JSON files. */
export function findReferences(files: ReadonlyMap<string, unknown>): FileReference[] {
  const out: FileReference[] = [];
  const root = { root: true } as const;
  const add = (file: string, path: PathSegment[], raw: unknown, kind: ReferenceKind, base: FileReference['base'] = root) => {
    if (typeof raw !== 'string' || /^(data:|blob:|https?:|\/\/)/.test(raw)) return;
    const target = 'folder' in base ? tryNormalize(base.folder + raw) : 'relativeToFile' in base ? tryResolve(file, raw) : tryNormalize(raw);
    if (target) out.push({ file, path, raw, target, kind, base });
  };
  const data = (file: string, value: unknown, path: PathSegment[]) => {
    for (const link of dataFileLinks(value, path)) add(file, link.path, link.raw, 'data');
  };
  /** An object's own links, other than its children. */
  const own = (file: string, o: Record<string, unknown>, here: PathSegment[]) => {
    if (o.type === 'prefab') add(file, [...here, 'prefab'], o.prefab, 'prefab');
    if (isObject(o.clips)) add(file, [...here, 'clips', 'file'], o.clips.file, 'clips');
    for (const field of ['data', 'scripts', 'overrides']) data(file, o[field], [...here, field]);
    filters(file, o.filters, [...here, 'filters']);
  };
  /** Shader filters name their .frag file. */
  const filters = (file: string, list: unknown, path: PathSegment[]) => {
    if (!Array.isArray(list)) return;
    list.forEach((f, i) => isObject(f) && f.type === 'shader' && add(file, [...path, i, 'shader'], f.shader, 'asset'));
  };
  const objects = (file: string, list: unknown, path: PathSegment[]) => {
    if (!Array.isArray(list)) return;
    for (const o of list) {
      if (!isObject(o) || typeof o.id !== 'string') continue;
      const here = [...path, { id: o.id }];
      own(file, o, here);
      objects(file, o.children, [...here, 'children']);
    }
  };

  const project = [...files].find(([f]) => fileKindFor(f) === 'project')?.[1];
  const themeFile = tryNormalize(themePathOf(isObject(project) ? (project as { theme?: string }) : undefined));
  const soundsFile = tryNormalize(soundsPathOf(isObject(project) ? (project as { sounds?: string }) : undefined));
  for (const [file, value] of files) {
    const kind = fileKindFor(file);
    if (kind === 'project' && isObject(value)) {
      if (Array.isArray(value.scenes)) value.scenes.forEach((s, i) => add(file, ['scenes', i], s, 'scene'));
      add(file, ['theme'], value.theme, 'theme');
      add(file, ['sounds'], value.sounds, 'sounds');
      data(file, value.settings, ['settings']);
    } else if (file === themeFile && isObject(value)) {
      if (isObject(value.fonts)) {
        for (const [name, font] of Object.entries(value.fonts)) {
          if (!isObject(font)) continue;
          for (const field of ['url', 'bitmap', 'texture']) add(file, ['fonts', name, field], font[field], 'asset');
        }
      }
    } else if (file === soundsFile && isObject(value)) {
      if (Array.isArray(value.preload)) value.preload.forEach((p, i) => add(file, ['preload', i], p, 'pack'));
      if (Array.isArray(value.loadLater)) value.loadLater.forEach((p, i) => add(file, ['loadLater', i], p, 'pack'));
    } else if (kind === 'scene' && isObject(value)) {
      if (Array.isArray(value.preload)) value.preload.forEach((p, i) => add(file, ['preload', i], p, 'pack'));
      if (Array.isArray(value.animations)) value.animations.forEach((a, i) => add(file, ['animations', i], a, 'animations'));
      if (Array.isArray(value.tilemaps)) value.tilemaps.forEach((t, i) => isObject(t) && add(file, ['tilemaps', i, 'file'], t.file, 'tilemap'));
      data(file, value.data, ['data']);
      if (isObject(value.camera)) filters(file, value.camera.filters, ['camera', 'filters']);
      objects(file, value.children, ['children']);
    } else if (kind === 'prefab' && isObject(value) && isObject(value.root)) {
      if (Array.isArray(value.tilemaps)) value.tilemaps.forEach((t, i) => isObject(t) && add(file, ['tilemaps', i, 'file'], t.file, 'tilemap'));
      own(file, value.root, ['root']);
      objects(file, value.root.children, ['root', 'children']);
    } else if (isPack(file, value)) {
      for (const [section, body] of Object.entries(value)) {
        if (section === 'meta' || !isObject(body) || !Array.isArray(body.files)) continue;
        if (typeof body.baseURL === 'string' && body.baseURL !== '') continue;
        const folder = typeof body.path === 'string' && body.path ? body.path.replace(/\/?$/, '/').replace(/^\/$/, '') : '';
        body.files.forEach((entry, i) => {
          if (!isObject(entry)) return;
          for (const field of PACK_URL_FIELDS) {
            const url = entry[field];
            // A sound or video in several formats lists one URL per format.
            if (Array.isArray(url)) url.forEach((u, j) => add(file, [section, 'files', i, field, j], u, 'asset', { folder }));
            else add(file, [section, 'files', i, field], url, 'asset', { folder });
          }
        });
      }
    } else if (isTiledMap(value)) {
      value.tilesets.forEach((ts, i) => isObject(ts) && add(file, ['tilesets', i, 'image'], ts.image, 'image', { relativeToFile: true }));
      const layers = (list: unknown[], path: PathSegment[]) =>
        list.forEach((l, i) => {
          if (!isObject(l)) return;
          if (l.type === 'imagelayer') add(file, [...path, i, 'image'], l.image, 'image', { relativeToFile: true });
          if (l.type === 'group' && Array.isArray(l.layers)) layers(l.layers, [...path, i, 'layers']);
        });
      layers(value.layers, ['layers']);
    }
  }
  return out;
}

function tryResolve(file: string, relative: string): string | undefined {
  try {
    return resolveFrom(file, relative);
  } catch {
    return undefined;
  }
}

/** References that name `target`, or anything inside it when it is a folder. */
export function referencesTo(files: ReadonlyMap<string, unknown>, target: string): FileReference[] {
  return findReferences(files).filter((r) => r.target === target || r.target.startsWith(target + '/'));
}

/** The moves that move a folder: every file under `from` goes to the same place under `to`. */
export function folderMoves(allFiles: Iterable<string>, from: string, to: string): FileMove[] {
  const prefix = from + '/';
  return [...allFiles].filter((f) => f.startsWith(prefix)).map((f) => ({ from: f, to: `${to}/${f.slice(prefix.length)}` }));
}

/** The ending that decides what kind of file a path is. A rename has to keep it. */
function kindEnding(path: string): string {
  const name = path.slice(dirname(path).length).replace(/^\//, '');
  for (const end of ['.scene.json', '.prefab.json', '.anims.json', '.clips.json', '.pack.json']) if (name.endsWith(end)) return end;
  return fileKindFor(path) === 'project' ? 'project.json' : name.includes('.') ? name.slice(name.lastIndexOf('.')).toLowerCase() : '';
}

export interface MovePlan {
  /** Every file that moves, normalised, folders expanded by the caller. */
  moves: FileMove[];
  /** The command: JSON files recreated at their new paths, then every rewritten reference. */
  ops: Op[];
  /** Files that aren't open JSON documents (images and the like). The project moves these in storage. */
  storageMoves: FileMove[];
  /** The references the command rewrites, by the file that holds them (its path before the move). */
  rewritten: FileReference[];
}

/**
 * Plans moving files: `moves` from old to new project paths. JSON files open
 * in `files` move as ops; others go in `storageMoves`. Refuses moves onto a
 * file that exists (`exists` says whether a path is taken by a file that
 * isn't open), moves that change a file's kind, and moving project.json.
 */
export function planMoves(files: ReadonlyMap<string, unknown>, moves: readonly FileMove[], exists: (path: string) => boolean = () => false): MovePlan {
  const to = new Map<string, string>();
  const targets = new Set<string>();
  for (const m of moves) {
    let from: string;
    let dest: string;
    try {
      from = normalizeProjectPath(m.from);
      dest = normalizeProjectPath(m.to);
    } catch (e) {
      throw new MoveError((e as Error).message);
    }
    if (from === dest) continue;
    if (fileKindFor(from) === 'project' || fileKindFor(dest) === 'project') throw new MoveError("project.json can't be moved or replaced");
    if (kindEnding(from) !== kindEnding(dest)) throw new MoveError(`${dest} has to keep the "${kindEnding(from)}" ending of ${from}`);
    if (to.has(from)) throw new MoveError(`${from} is moved twice`);
    if (targets.has(dest)) throw new MoveError(`Two files would move to ${dest}`);
    to.set(from, dest);
    targets.add(dest);
  }
  for (const dest of targets) {
    // A file that moves away frees its path for another file in the same command.
    if (!to.has(dest) && (files.has(dest) || exists(dest))) throw new MoveError(`${dest} already exists`);
  }
  const ops: Op[] = [];
  const storageMoves: FileMove[] = [];
  const recreated: Op[] = [];
  const deleted: Op[] = [];
  for (const [from, dest] of to) {
    if (files.has(from)) {
      recreated.push({ op: 'createFile', file: dest, value: files.get(from) });
      deleted.push({ op: 'deleteFile', file: from });
    } else {
      storageMoves.push({ from, to: dest });
    }
  }
  // Deletes first, so a file can take a path another file in this command frees.
  ops.push(...deleted, ...recreated);

  const { ops: refOps, rewritten } = referenceOps(files, to);
  ops.push(...refOps);
  return { moves: [...to].map(([from, dest]) => ({ from, to: dest })), ops, storageMoves, rewritten };
}

/** Ops that point every reference to some files at others, of any kind, such as a picture at the compressed copy that takes its place. */
export function repointReferences(files: ReadonlyMap<string, unknown>, to: ReadonlyMap<string, string>): { ops: Op[]; rewritten: FileReference[] } {
  return referenceOps(files, to);
}

/** Ops that point every reference at a moved file's new path. `to` maps old paths to new ones, for files and for files holding references. */
function referenceOps(files: ReadonlyMap<string, unknown>, to: ReadonlyMap<string, string>): { ops: Op[]; rewritten: FileReference[] } {
  const newPath = (p: string) => to.get(p) ?? p;
  const ops: Op[] = [];
  const rewritten: FileReference[] = [];
  for (const ref of findReferences(files)) {
    const ownerMoved = to.has(ref.file);
    const targetMoved = to.has(ref.target);
    if (!targetMoved && !(ownerMoved && 'relativeToFile' in ref.base)) continue;
    const target = newPath(ref.target);
    const value =
      'folder' in ref.base ? relativePath(ref.base.folder, target) : 'relativeToFile' in ref.base ? relativePath(dirname(newPath(ref.file)), target) : target;
    if (value === ref.raw) continue;
    rewritten.push(ref);
    ops.push({ op: 'set', file: newPath(ref.file), path: ref.path, value });
  }
  return { ops, rewritten };
}

/**
 * Repoints references at files that were moved outside the editor (by an
 * agent, a script or the file manager): each `from` is a path references
 * still name that no longer exists, `to` the file that is there now.
 */
export function relinkOps(files: ReadonlyMap<string, unknown>, relinks: readonly FileMove[], exists: (path: string) => boolean): { ops: Op[]; rewritten: FileReference[] } {
  const to = new Map<string, string>();
  for (const r of relinks) {
    if (exists(r.from)) throw new MoveError(`${r.from} still exists, so its links aren't broken`);
    if (!exists(r.to)) throw new MoveError(`${r.to} does not exist`);
    if (kindEnding(r.from) !== kindEnding(r.to)) throw new MoveError(`${r.to} isn't the same kind of file as ${r.from}`);
    to.set(r.from, r.to);
  }
  return referenceOps(files, to);
}

const baseName = (p: string) => p.slice(dirname(p).length).replace(/^\//, '');

/**
 * Guesses where missing files went: for each path that references name but
 * no file has, the one unreferenced file with the same name, or with the
 * same contents (`sameContents`, for files whose old contents are known).
 * Paths with no single answer are left out, for the user to fix by hand.
 */
export function suggestRelinks(files: ReadonlyMap<string, unknown>, allFiles: readonly string[], sameContents: (missing: string, candidate: string) => boolean = () => false): FileMove[] {
  const all = new Set(allFiles);
  // A content pack's files name game paths: the pack's own copy counts, else the game's.
  const refs = findReferences(files).map((r) => {
    const pack = /^packs\/[^/]+\//.exec(r.file)?.[0];
    return pack && !all.has(r.target) && all.has(pack + r.target) ? { ...r, target: pack + r.target } : r;
  });
  const referenced = new Set(refs.map((r) => r.target).filter((t) => all.has(t)));
  const missing = [...new Set(refs.map((r) => r.target).filter((t) => !all.has(t)))].sort();
  const free = allFiles.filter((f) => !referenced.has(f) && fileKindFor(f) !== 'project' && !f.startsWith('packs/'));
  const out: FileMove[] = [];
  const taken = new Set<string>();
  for (const m of missing) {
    const byContents = free.filter((f) => sameContents(m, f));
    const candidates = byContents.length ? byContents : free.filter((f) => baseName(f) === baseName(m) && kindEnding(f) === kindEnding(m));
    if (candidates.length === 1 && !taken.has(candidates[0]!)) {
      out.push({ from: m, to: candidates[0]! });
      taken.add(candidates[0]!);
    }
  }
  return out;
}
